import React from "react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * The methodology disclosure, rendered from the wire.
 *
 * The frontend STRUCTURES this text and does not author it. Every sentence comes
 * from `data.methodology`; keeping a second copy here is how a UI ends up
 * describing a computation the backend no longer performs. If the block is
 * missing the trigger does not render at all — an empty popover would be better
 * than a wrong one, and no popover is better than both.
 *
 * It sits inside the "Erwartetes Objektergebnis" KPI card rather than beside the
 * tab heading, because it explains THAT figure: what is in the cost basis, what
 * is deliberately not, and why the result is not a company profit.
 */

const SECTION_LABEL =
  "text-[10px] font-medium uppercase tracking-[0.8px] text-muted-foreground";

const METHODOLOGY_SECTIONS = ["revenue", "costs", "result", "occupancy", "limitations"];

export default function PerformanceMethodologyPopover({ methodology }) {
  if (!methodology) return null;

  const sections = METHODOLOGY_SECTIONS.map((key) => methodology[key]).filter(
    (section) => section && Array.isArray(section.paragraphs) && section.paragraphs.length
  );
  if (!sections.length) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Methodik: Erwartetes Objektergebnis"
          data-testid="performance-methodology-trigger"
          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-border text-[9px] font-semibold leading-none text-muted-foreground transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          i
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 max-h-[60vh] overflow-y-auto"
        data-testid="performance-methodology-content"
      >
        <p className="mb-2 text-[12px] font-medium text-popover-foreground">
          {methodology.title}
        </p>
        <div className="space-y-2">
          {sections.map((section) => (
            <div key={section.title}>
              <p className={SECTION_LABEL}>{section.title}</p>
              {section.paragraphs.map((paragraph) => (
                <p
                  key={paragraph}
                  className="mt-1 text-[11px] leading-[1.45] text-muted-foreground"
                >
                  {paragraph}
                </p>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
